import { useState } from "react";
import { toast } from "sonner";
import { ArrowRight, Check, Loader2 } from "lucide-react";
import { api } from "@/lib/site";

const inputCls =
  "w-full bg-transparent border-0 border-b border-dc-borderHi py-2.5 text-white placeholder:text-dc-muted focus:border-dc-accent focus:outline-none transition-colors";
const labelCls = "block text-xs uppercase tracking-wider text-dc-text2 mb-2";

const Field = ({ label, children }) => (
  <div><label className={labelCls}>{label}</label>{children}</div>
);

const types = ["Reprocessor / Paper Mill", "Exporter / Material Trader", "Waste & Recycling Facility", "Haulier / Logistics"];

export const PartnerForm = ({ defaultType = types[0] }) => {
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [form, setForm] = useState({
    partner_type: defaultType, company: "", contact: "", position: "", email: "", telephone: "",
    operating_regions: "", facility_carrier_type: "", materials: "", typical_capacity: "", permit_number: "", message: "",
  });
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.company || !form.contact || !form.email || !form.telephone) { toast.error("Please complete the required fields"); return; }
    setSubmitting(true);
    try {
      await api.post("/partner-enquiry", form);
      setDone(true);
      toast.success("Partner enquiry received");
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally { setSubmitting(false); }
  };

  if (done) {
    return (
      <div data-testid="partner-success" className="border border-dc-border bg-dc-surface p-10 text-center">
        <div className="w-14 h-14 mx-auto bg-dc-accent flex items-center justify-center mb-6"><Check className="text-dc-bg" size={28} /></div>
        <h3 className="font-display text-2xl mb-3">Enquiry Received</h3>
        <p className="text-dc-text2 max-w-md mx-auto">Thank you. We will review your capabilities and be in touch to discuss opportunities across the network.</p>
      </div>
    );
  }

  return (
    <form data-testid="partner-form" onSubmit={submit} className="border border-dc-border bg-dc-surface p-6 lg:p-10">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
        <div className="sm:col-span-2">
          <Field label="Partner type"><select data-testid="pf-type" className={inputCls} value={form.partner_type} onChange={set("partner_type")}>{types.map((t) => <option key={t} className="bg-dc-surface">{t}</option>)}</select></Field>
        </div>
        <Field label="Company"><input data-testid="pf-company" className={inputCls} value={form.company} onChange={set("company")} /></Field>
        <Field label="Contact"><input data-testid="pf-contact" className={inputCls} value={form.contact} onChange={set("contact")} /></Field>
        <Field label="Position"><input data-testid="pf-position" className={inputCls} value={form.position} onChange={set("position")} /></Field>
        <Field label="Email"><input data-testid="pf-email" type="email" className={inputCls} value={form.email} onChange={set("email")} /></Field>
        <Field label="Telephone"><input data-testid="pf-phone" className={inputCls} value={form.telephone} onChange={set("telephone")} /></Field>
        <Field label="Operating regions"><input data-testid="pf-regions" className={inputCls} value={form.operating_regions} onChange={set("operating_regions")} /></Field>
        <Field label="Facility / carrier type"><input data-testid="pf-facility" className={inputCls} value={form.facility_carrier_type} onChange={set("facility_carrier_type")} /></Field>
        <Field label="Materials accepted / transported"><input data-testid="pf-materials" className={inputCls} value={form.materials} onChange={set("materials")} /></Field>
        <Field label="Typical capacity"><input data-testid="pf-capacity" className={inputCls} value={form.typical_capacity} onChange={set("typical_capacity")} placeholder="e.g. tonnes/month or loads/week" /></Field>
        <Field label="Environmental permit / registration no."><input data-testid="pf-permit" className={inputCls} value={form.permit_number} onChange={set("permit_number")} /></Field>
        <div className="sm:col-span-2">
          <Field label="Message"><textarea data-testid="pf-message" rows={3} className={inputCls} value={form.message} onChange={set("message")} /></Field>
        </div>
      </div>
      <button data-testid="pf-submit" disabled={submitting} className="mt-8 inline-flex items-center gap-2 bg-dc-accent text-dc-bg font-semibold px-6 py-3 hover:bg-dc-accentHover transition-colors text-sm disabled:opacity-60">
        {submitting ? <><Loader2 size={16} className="animate-spin" /> Submitting…</> : <>Submit Enquiry <ArrowRight size={16} /></>}
      </button>
    </form>
  );
};
